import { useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { getSubmissions, downloadExport } from "@/lib/api";
import { SectionLabel } from "@/components/motion";

const TABS = [
  { id: "quotes", label: "Quote Requests" },
  { id: "carriers", label: "Carrier Applications" },
  { id: "messages", label: "Messages" },
];

function Table({ rows }) {
  if (!rows.length) return <p className="p-6 font-mono text-xs uppercase tracking-[0.2em] text-dim">No submissions yet.</p>;
  const cols = Object.keys(rows[0]).filter((k) => k !== "id");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-line">
            {cols.map((c) => (
              <th key={c} className="whitespace-nowrap px-4 py-3 font-mono uppercase tracking-[0.15em] text-brass">{c.replace(/_/g, " ")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-line/50 hover:bg-panel-hover">
              {cols.map((c) => (
                <td key={c} className="max-w-[220px] truncate px-4 py-3 text-[#C9CDD4]">{String(r[c] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("elt_admin") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("quotes");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const res = await getSubmissions(k);
      setData(res.data);
      setKey(k);
      sessionStorage.setItem("elt_admin", k);
    } catch {
      toast.error("Invalid admin key.");
      sessionStorage.removeItem("elt_admin");
      setKey("");
    } finally {
      setLoading(false);
    }
  };

  const onLogin = (e) => {
    e.preventDefault();
    load(input);
  };

  const onExport = async () => {
    try {
      await downloadExport(key);
      toast.success("Excel file downloaded — ready for your CRM import.");
    } catch {
      toast.error("Export failed.");
    }
  };

  if (!key || !data) {
    return (
      <main className="flex min-h-screen items-center justify-center px-5 pt-16" data-testid="admin-login">
        <form onSubmit={onLogin} className="w-full max-w-sm border border-line bg-panel">
          <div className="hazard-stripes h-1.5 w-full" />
          <div className="p-8">
            <SectionLabel>// Restricted</SectionLabel>
            <h1 className="mt-2 font-display text-2xl font-extrabold uppercase text-bright">Submissions Desk</h1>
            <input
              data-testid="admin-password-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin key"
              className="mt-6 w-full border border-line bg-asphalt px-4 py-3 text-sm text-bright outline-none focus:border-hazard"
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="mt-4 w-full bg-hazard px-4 py-3 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors hover:bg-hazard-dark disabled:opacity-50"
            >
              {loading ? "Verifying…" : "Access Dashboard"}
            </button>
          </div>
        </form>
      </main>
    );
  }

  const counts = { quotes: data.quotes.length, carriers: data.carriers.length, messages: data.messages.length };

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-5 pb-20 pt-28 lg:px-8" data-testid="admin-dashboard">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <SectionLabel>// Submissions Desk</SectionLabel>
          <h1 className="mt-2 font-display text-3xl font-extrabold uppercase text-bright sm:text-4xl">CRM Export Center</h1>
          <p className="mt-2 text-sm text-[#9CA3AF]">
            {counts.quotes} quotes · {counts.carriers} carriers · {counts.messages} messages
          </p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-btn"
            onClick={() => load(key)}
            className="flex items-center gap-2 border border-line-light px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-bright transition-colors hover:border-hazard hover:text-hazard"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            data-testid="admin-export-btn"
            onClick={onExport}
            className="flex items-center gap-2 bg-hazard px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-asphalt transition-colors hover:bg-hazard-dark"
          >
            <Download size={14} /> Export .xlsx
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={() => { sessionStorage.removeItem("elt_admin"); setKey(""); setData(null); }}
            className="flex items-center gap-2 border border-line-light px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-dim transition-colors hover:text-bright"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>

      <div className="mt-8 flex gap-2 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
              tab === t.id ? "border-b-2 border-hazard text-hazard" : "text-dim hover:text-bright"
            }`}
          >
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      <div className="mt-6 border border-line bg-panel" data-testid="admin-table">
        <Table rows={data[tab]} />
      </div>
    </main>
  );
}
